/**
 * Réglages pilotés par le client depuis l'admin distante.
 *
 * Ils vivent hors des packs de thème : ceux-ci sont écrasés à chaque synchro.
 * L'admin les écrit dans Firestore, la borne les écoute et en garde une copie
 * locale (`data/admin.json`) pour démarrer hors ligne. Les fichiers associés
 * (logos, vidéos) passent par Cloud Storage et sont recopiés dans `data/`.
 *
 * Fichier partagé à l'identique avec le dépôt de l'admin — voir `cloud.ts`.
 */
import { DEFAULT_LED_SETTINGS, sanitizeLedSettings, type LedSettings } from './leds.ts';

export type Corner = 'bottom-left' | 'bottom-center' | 'bottom-right' | 'top-left' | 'top-right';

export interface DateStampSettings {
  enabled: boolean;
  /** `auto` suit la locale, sinon le texte est repris tel quel. */
  mode: 'auto' | 'custom';
  customText: string;
  /** Options Intl, en mode `auto`. */
  includeWeekday: boolean;
  includeTime: boolean;
  position: Corner;
  /** Fraction de la hauteur d'image. */
  fontScale: number;
  color: string;
  /** Bandeau sombre derrière le texte, pour la lisibilité sur fond clair. */
  shadow: boolean;
  uppercase: boolean;
}

export interface LogoSettings {
  /** Nom de fichier dans `data/logos/` sur la borne, `<préfixe>/logos/` dans le bucket. */
  file: string;
  position: Corner;
  /** Hauteur du logo, en fraction de la hauteur d'image. */
  scale: number;
  opacity: number;
}

/**
 * Logo de la marque fourni par le pack de thème — le logo Thales. C'est le
 * « deuxième logo » de la photo : rien à envoyer, seulement le poser ou non,
 * et où. Le fichier est celui du thème (`resultLogoRightFile`, à défaut
 * `logoFile`).
 */
export interface ThemeLogoSettings {
  enabled: boolean;
  position: Corner;
  /** Hauteur, en fraction de la hauteur d'image. */
  scale: number;
  opacity: number;
}

/**
 * Éclairage donné par l'écran pendant la prise de vue.
 *
 * `ring` : un anneau lumineux autour du retour caméra, allumé dès le début du
 * décompte — la caméra a le temps de régler son exposition, et la lumière
 * frontale douce flatte les visages, comme une ring light. `screen` : l'écran
 * entier blanc au « 0 », l'ancien flash ; il éblouit et surexpose souvent.
 * `off` : rien, l'éclairage vient du stand (et du ruban LED).
 */
export type LightingMode = 'ring' | 'screen' | 'off';

export interface LightingSettings {
  mode: LightingMode;
  color: string;
  /** 0,2 à 1 — pendant le décompte ; l'anneau passe à pleine intensité au « 0 ». */
  intensity: number;
  /** Épaisseur de l'anneau, en fraction de la hauteur d'écran. */
  thickness: number;
}

export interface ScreensaverSettings {
  enabled: boolean;
  /** Secondes d'inactivité sur l'accueil avant le passage en veille. */
  idleSeconds: number;
  /** Noms de fichiers dans `data/screensaver/` (`<préfixe>/screensaver/` dans le bucket), joués en boucle dans l'ordre. */
  videos: string[];
  muted: boolean;
}

/**
 * Fond posé derrière les rendus détourés.
 *
 * Sans fond, un rendu `cutout` sort en PNG transparent, pour un compositing
 * ultérieur. Avec un fond, la borne compose elle-même la photo finale ; le PNG
 * transparent reste conservé à côté, dans `captures/`.
 */
export interface CutoutBackgroundSettings {
  enabled: boolean;
  /** Nom de fichier dans `data/backgrounds/` sur la borne, `<préfixe>/backgrounds/` dans le bucket. */
  file: string;
  /**
   * Harmonisation de la personne avec le fond, de 0 (collage brut) à 1 : teintes
   * et lumière rapprochées de celles du décor, halo du décor sur les contours.
   */
  blend: number;
}

export interface AdminSettings {
  /**
   * L'incrustation ne s'applique qu'aux rendus `scene` : un PNG détouré doit
   * rester propre, le client posera fond, logos et date au compositing.
   */
  dateStamp: DateStampSettings;
  logos: LogoSettings[];
  themeLogo: ThemeLogoSettings;
  lighting: LightingSettings;
  screensaver: ScreensaverSettings;
  cutoutBackground: CutoutBackgroundSettings;
  /** Ruban LED : repos, déroulé du décompte, flash de la prise de vue. */
  leds: LedSettings;
  updatedAt?: string;
}

export const DEFAULT_ADMIN_SETTINGS: AdminSettings = {
  dateStamp: {
    enabled: false,
    mode: 'auto',
    customText: '',
    includeWeekday: true,
    includeTime: false,
    position: 'bottom-center',
    fontScale: 0.032,
    color: '#FFFFFF',
    shadow: true,
    uppercase: true,
  },
  logos: [],
  themeLogo: {
    enabled: true,
    position: 'bottom-right',
    scale: 0.065,
    opacity: 1,
  },
  lighting: {
    mode: 'ring',
    color: '#FFF1DC',
    intensity: 0.85,
    thickness: 0.09,
  },
  screensaver: {
    enabled: false,
    idleSeconds: 60,
    videos: [],
    muted: true,
  },
  cutoutBackground: {
    enabled: false,
    file: '',
    blend: 0.35,
  },
  leds: DEFAULT_LED_SETTINGS,
};

/** Au-delà, la bande d'incrustation n'a plus de sens et la mise en page dégénère. */
export const MAX_LOGOS = 8;

/**
 * Bornes appliquées par l'admin à l'écriture, puis par la borne à la réception :
 * un document modifié à la main dans la console Firestore ne doit pas casser le
 * rendu.
 */
export function sanitizeAdminSettings(settings: AdminSettings): AdminSettings {
  const clamp = (v: number, lo: number, hi: number, fallback: number) =>
    Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback;

  settings.logos = (Array.isArray(settings.logos) ? settings.logos : [])
    .slice(0, MAX_LOGOS)
    .map((logo) => ({
      ...logo,
      scale: clamp(logo.scale, 0.02, 0.3, 0.09),
      opacity: clamp(logo.opacity, 0.05, 1, 1),
    }));

  const themeLogo = settings.themeLogo ?? DEFAULT_ADMIN_SETTINGS.themeLogo;
  settings.themeLogo = {
    enabled: Boolean(themeLogo.enabled),
    position: CORNERS.includes(themeLogo.position) ? themeLogo.position : 'bottom-right',
    scale: clamp(themeLogo.scale, 0.02, 0.3, 0.065),
    opacity: clamp(themeLogo.opacity, 0.05, 1, 1),
  };

  const lighting = settings.lighting ?? DEFAULT_ADMIN_SETTINGS.lighting;
  settings.lighting = {
    mode: lighting.mode === 'screen' || lighting.mode === 'off' ? lighting.mode : 'ring',
    color: /^#[0-9a-f]{6}$/i.test(lighting.color) ? lighting.color : DEFAULT_ADMIN_SETTINGS.lighting.color,
    intensity: clamp(lighting.intensity, 0.2, 1, 0.85),
    thickness: clamp(lighting.thickness, 0.03, 0.2, 0.09),
  };

  settings.dateStamp.fontScale = clamp(settings.dateStamp.fontScale, 0.01, 0.09, 0.032);
  settings.screensaver.idleSeconds = clamp(settings.screensaver.idleSeconds, 10, 3600, 60);
  if (!Array.isArray(settings.screensaver.videos)) settings.screensaver.videos = [];
  settings.cutoutBackground = {
    enabled: Boolean(settings.cutoutBackground?.enabled),
    file: typeof settings.cutoutBackground?.file === 'string' ? settings.cutoutBackground.file : '',
    blend: clamp(Number(settings.cutoutBackground?.blend), 0, 1, 0.35),
  };
  settings.leds = sanitizeLedSettings(settings.leds);
  return settings;
}

const CORNERS: Corner[] = ['bottom-left', 'bottom-center', 'bottom-right', 'top-left', 'top-right'];

/** Préfixe des fichiers de logo venus du pack de thème, pour les distinguer de ceux de l'admin. */
export const THEME_LOGO_PREFIX = 'theme:';

/**
 * Logos à incruster : celui du thème s'il est activé, puis ceux de l'admin.
 * Une seule liste pour le moteur de bandes, qui les pose sans chevauchement.
 */
export function brandingLogos(settings: AdminSettings, themeLogoFile: string | undefined): LogoSettings[] {
  const { enabled, position, scale, opacity } = settings.themeLogo;
  if (!enabled || !themeLogoFile) return settings.logos;
  return [{ file: THEME_LOGO_PREFIX + themeLogoFile, position, scale, opacity }, ...settings.logos];
}

/** Libellé de date rendu sur la photo. */
export function formatDateStamp(settings: DateStampSettings, now: Date, locale = 'fr-FR'): string {
  if (settings.mode === 'custom') {
    return settings.uppercase ? settings.customText.toUpperCase() : settings.customText;
  }

  const text = new Intl.DateTimeFormat(locale, {
    weekday: settings.includeWeekday ? 'long' : undefined,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: settings.includeTime ? '2-digit' : undefined,
    minute: settings.includeTime ? '2-digit' : undefined,
  }).format(now);

  return settings.uppercase ? text.toUpperCase() : text;
}
